import { Accordion, Box, Flex, Radio, Space, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { TableHeader } from '../UI/common';
import EventsSvg from './Events.svg?react';
import FirstGraphSvg from './FirstGraph.svg?react';
import useExpenditures from './hooks/useExpenditures';
import useIncomes from './hooks/useIncomes';
import useLifeEvents from './hooks/useLifeEvents';
import NotesSvg from './Notes.svg?react';
import SecondGraphSvg from './SecondGraph.svg?react';
import PageLoader from 'src/UI/loader/PageLoader';
import { formatCurrency, Frequency } from './utils';
import { IncomePanel } from './form/IncomePanel';
import { ExpenditurePanel } from './form/ExpenditurePanel';
import { LifeEventPanel } from './form/LifeEventPanel';

const ACCORDION_CHEVRON = <IconChevronDown size={20} stroke={1.5} />;

export default function CashflowContent() {
  const {
    isLoading: loadingIncomes,
    incomesData,
    upsertIncomes,
    deleteIncomes,
    frequency,
    setFrequency,
    totalPreTax: incomesTotalPreTax,
    totalPostTax: incomesTotalPostTax,
  } = useIncomes();

  const {
    isLoading: loadingExpenditures,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();
  const { isCrawford } = useIsAfternoon();
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const amountKey = frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';
  const expendituresTotal = expendituresData?.reduce((sum, e) => sum + e[amountKey], 0) ?? 0;
  const lifeEventsTotal = lifeEventsData?.reduce((sum, e) => sum + (e.cost || 0), 0) ?? 0;
  const netPosition = incomesTotalPostTax - expendituresTotal;
  const isNetPositive = netPosition >= 0;

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  return (
    <Box>
      <Flex justify="flex-end" px="md" mb="xs">
        <Radio.Group value={frequency} onChange={value => setFrequency(value as Frequency)}>
          <Flex gap="xs">
            <Radio value="monthly" label="Monthly" size="xs" />
            <Radio value="annually" label="Annually" size="xs" />
          </Flex>
        </Radio.Group>
      </Flex>

      <TableHeader
        columns={[
          { label: 'Type', align: 'left' },
          { label: 'Total', align: 'right' },
        ]}
        pr="56px"
      />

      <Accordion
        defaultValue={null}
        value={activePanel}
        onChange={setActivePanel}
        variant="cells"
        chevron={ACCORDION_CHEVRON}
      >
        <Accordion.Item value="incomes" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Income"
              count={incomesData?.length ?? 0}
              amount={`${formatCurrency(incomesTotalPreTax)} (${formatCurrency(incomesTotalPostTax)} after tax)`}
              signPrefix="+"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <IncomePanel
              items={incomesData ?? []}
              onSave={upsertIncomes}
              onDelete={deleteIncomes}
              frequency={frequency}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="expenditures" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Expenditure"
              count={expendituresData?.length ?? 0}
              amount={formatCurrency(expendituresTotal)}
              signPrefix="-"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <ExpenditurePanel
              items={expendituresData ?? []}
              onSave={upsertExpenditures}
              onDelete={deleteExpenditures}
              frequency={frequency}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Flex
        px="md"
        py="sm"
        justify="space-between"
        align="center"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Text size="lg" fw={700}>
          Net Position
        </Text>
        <Text
          size="lg"
          fw={700}
          c={isNetPositive ? 'green.6' : 'red.6'}
          data-testid="net-position-amount"
          pr="36px"
        >
          {isNetPositive ? '+' : '-'}
          {formatCurrency(Math.abs(netPosition))}
        </Text>
      </Flex>

      <Accordion
        value={activePanel}
        onChange={setActivePanel}
        variant="cells"
        chevron={ACCORDION_CHEVRON}
        mt="md"
      >
        <Accordion.Item value="lifeEvents" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Future life events"
              count={lifeEventsData?.length ?? 0}
              amount={formatCurrency(lifeEventsTotal)}
              amountLabel="Total"
              signPrefix="-"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <LifeEventPanel
              items={lifeEventsData ?? []}
              onSave={upsertLifeEvents}
              onDelete={deleteLifeEvents}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {isCrawford && (
        <Box pr="md">
          <Text size="xl" mt="xxxxl">
            Key events
          </Text>
          <Text size="sm">Last updated: 4 minutes ago</Text>
          <Space my="xxl" />
          <EventsSvg />
          <Space my="xxl" />
          <FirstGraphSvg />
          <Space my="xxl" />
          <SecondGraphSvg />
          <Space my="xxl" />
          <NotesSvg />
        </Box>
      )}
    </Box>
  );
}

type AccordionHeaderProps = {
  title: string;
  count?: number;
  amount: string;
  amountLabel?: string;
  signPrefix?: string;
};

function AccordionHeader({
  title,
  count,
  amount,
  amountLabel,
  signPrefix = '',
}: AccordionHeaderProps) {
  const testId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <Flex justify="space-between">
      <Box>
        {count !== undefined && (
          <Text size="sm" data-testid={`${testId}-count`}>
            {count} {count === 1 ? 'source' : 'sources'}
          </Text>
        )}
        <Text size="lg" fw={700}>
          {title}
        </Text>
      </Box>
      <Flex direction="column" align="flex-end" justify="center">
        {amountLabel && <Text size="sm">{amountLabel}</Text>}
        <Text size="lg" fw={700} data-testid={`${testId}-amount`}>
          {signPrefix}
          {amount}
        </Text>
      </Flex>
    </Flex>
  );
}
